import { CET_SECTIONS, ASSETS } from "../mock";
import { useTexts } from "../hooks/useTexts";
import SectionTitle from "../components/SectionTitle";
import PageSeo from "../components/PageSeo";
import Reveal from "../components/Reveal";
import FresnoLeaf from "../components/FresnoLeaf";
import HtmlOrText from "../components/HtmlOrText";
import { ExternalLink, BookOpen } from "lucide-react";
import { useEffect, useState } from "react";

export default function CET() {
  const t = useTexts();
  const [activeId, setActiveId] = useState(CET_SECTIONS[0].id);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    CET_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <div>
      <PageSeo
        title="¿Qué es el C.E.T.?"
        description="Toda la información sobre el Complejo de Esclerosis Tuberosa (CET): causas genéticas, síntomas, diagnóstico y tratamientos disponibles."
        path="/cet"
      />
      <section className="max-w-7xl mx-auto px-6 relative">
        {/* Decorative fresno branches — SVG, no butterflies */}
        <FresnoLeaf className="hidden lg:block absolute left-2 top-4 w-24 opacity-55 -rotate-12 pointer-events-none animate-floaty" />
        <FresnoLeaf className="hidden lg:block absolute right-8 top-[540px] w-20 opacity-50 rotate-[135deg] pointer-events-none animate-floaty-slow" />

        <SectionTitle>¿qué es el c.e.t.?</SectionTitle>

        {/* Video + TOC */}
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl ring-4 ring-white bg-black">
                <iframe
                  src="https://drive.google.com/file/d/1ZHpr-uOJwYTGxCOj-Dk3O0RXZHqJmmrp/preview"
                  title="Entrevista con Laura Soto"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <p className="mt-3 text-sm text-[color:var(--text-soft)] italic text-center">
                Entrevista con Laura Soto en Información.tv, Alicante · 29/12/2025
              </p>
            </Reveal>

            <div className="mt-10 max-w-3xl space-y-5">
              <Reveal><HtmlOrText html={t.cet_intro_1} /></Reveal>
              <Reveal delay={100}><HtmlOrText html={t.cet_intro_2} /></Reveal>
              <div>
                <a
                  href="https://www.canva.com/design/DAGYEMYkXbA/Xi0mxoi6BwFIqQAYl2EabQ/view"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-brand inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold shadow"
                >
                  Folleto de información <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* TOC sticky */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <div className="pf-soft rounded-2xl p-5 relative overflow-hidden">
              <FresnoLeaf className="absolute -right-4 -top-4 w-20 opacity-55 rotate-[25deg] pointer-events-none" />
              <h3 className="h-serif text-lg font-semibold text-[color:var(--brand)] mb-3 relative">Contenido</h3>
              <ul className="space-y-1">
                {CET_SECTIONS.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`toc-item flex items-start gap-3 p-2 rounded-lg text-sm transition-colors hover:bg-white/60 ${activeId === s.id ? "active" : "text-[color:var(--text)]"}`}
                    >
                      <span className="toc-num font-cursive text-lg leading-none w-6 shrink-0 text-[color:var(--text-mute)]">{`0${i + 1}`}</span>
                      <span className="leading-tight pt-0.5">{s.subtitle}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* Sections */}
        <div className="mt-20 space-y-24 max-w-4xl">
          {CET_SECTIONS.map((s, idx) => (
            <article key={s.id} id={s.id} className="scroll-mt-32 relative">
              {/* Decorative fresno leaf accents (alternating sides) */}
              {idx % 2 === 0 ? (
                <FresnoLeaf className="hidden md:block absolute -right-24 -top-4 w-24 opacity-55 rotate-[15deg] pointer-events-none" />
              ) : (
                <FresnoLeaf className="hidden md:block absolute -left-28 -top-2 w-28 opacity-50 rotate-[-160deg] pointer-events-none animate-floaty-slow" />
              )}
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <img src={ASSETS.smallLeaf} alt="" className={`w-10 ${idx % 2 ? "-scale-x-100" : ""}`} />
                  <h3 className="h-serif text-3xl sm:text-4xl font-semibold text-[color:var(--brand-strong)]">{s.subtitle}</h3>
                </div>

                {s.body && <p className="prose-body text-[color:var(--text)]">{s.body}</p>}

                {s.intro && <p className="prose-body text-[color:var(--text)] mb-6">{s.intro}</p>}

                {s.resources && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                    {s.resources.map((r, ridx) => (
                      <div key={r.id} className="pf-soft rounded-2xl p-6 hover-lift flex flex-col relative overflow-hidden">
                        <FresnoLeaf
                          className={`absolute -right-3 -top-3 w-16 opacity-55 pointer-events-none ${ridx % 2 ? "rotate-[35deg]" : "-rotate-[20deg]"}`}
                        />
                        <div className="inline-flex items-center gap-2 mb-2 relative">
                          <span className="p-2 rounded-full bg-white shadow-sm">
                            <BookOpen className="w-5 h-5 text-[color:var(--brand)]" />
                          </span>
                          <h4 className="h-serif text-2xl font-semibold text-[color:var(--brand-strong)]">{r.name}</h4>
                        </div>
                        <p className="prose-body text-sm text-[color:var(--text)] flex-1">{r.summary}</p>
                        {r.highlights?.length > 0 && (
                          <ul className="space-y-2 mt-3">
                            {r.highlights.map((h, k) => (
                              <li key={k} className="flex gap-2 text-sm text-[color:var(--text)]">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--brand-light)] shrink-0" />
                                <span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {r.links?.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {r.links.map((l, k) => (
                              <a
                                key={k}
                                href={l.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[color:var(--brand-lighter)] hover:bg-[color:var(--bg-mid)] text-[color:var(--brand)]"
                              >
                                {l.label} <ExternalLink className="w-3 h-3" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {s.twoCols && (
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    {s.twoCols.map((c, i) => (
                      <div key={i} className="pf-soft rounded-2xl p-6 hover-lift">
                        <h4 className="font-cursive text-2xl text-[color:var(--brand)] mb-3">{c.heading}</h4>
                        {c.text && <p className="prose-body text-[color:var(--text)] text-sm">{c.text}</p>}
                        {c.list && (
                          <ul className="space-y-2 mt-1">
                            {c.list.map((li, j) => (
                              <li key={j} className="flex gap-2 text-[color:var(--text)] text-sm">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--brand-light)] shrink-0" />
                                <span>{li}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {s.list && !s.twoCols && (
                  <ul className="space-y-3 mt-6">
                    {s.list.map((li, j) => (
                      <li key={j} className="flex gap-3 text-[color:var(--text)]">
                        <span className="mt-2 w-2 h-2 rounded-full bg-[color:var(--brand-light)] shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {s.footer && (
                  <p className="prose-body mt-6 text-[color:var(--text)] italic">{s.footer}</p>
                )}
              </Reveal>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
